import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import ErrorIcon from '@watershed/icons/components/Error';
import mixinSx from '@watershed/style/mixinSx';

import CircularProgress from '../../CircularProgress';
import { getFileTypeIcon } from '../fileIcons';
import { IconElement } from '@watershed/icons/Icon';

export type FileItemRowProps = React.PropsWithChildren<{
  icon: 'success' | 'error' | 'loading' | IconElement;
  title: LocalizedString;
  endAdornment?: React.ReactNode;
  subtitle: LocalizedString;
  actions: React.ReactNode;
  progress?: number;
}>;

/**
 * Internal component to `FileUploadDropzone`, used to show a generic file row with status in the
 * upload file table. Uses a status icon for success, error, or loading, and shows a progress bar
 * if the file is currently uploading. Also adds some metadata about the file, and allows for
 * actions/children to be passed in.
 */
export function FileItemRow({
  icon,
  title,
  endAdornment,
  subtitle,
  actions,
  progress,
  children,
}: FileItemRowProps) {
  const inProgress =
    icon === 'loading' &&
    progress !== undefined &&
    progress > 0 &&
    progress < 100;

  const IconOverride = typeof icon === 'string' ? null : icon;
  const FileIcon = icon === 'success' ? getFileTypeIcon(String(title)) : null;

  const iconComponent = IconOverride ? (
    <IconOverride color="grey50" />
  ) : icon === 'success' && FileIcon ? (
    <FileIcon color="grey50" />
  ) : icon === 'error' ? (
    <ErrorIcon color="error.dark" />
  ) : inProgress ? (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignSelf: 'center',
      }}
    >
      <CircularProgress
        sx={{ color: (theme) => theme.palette.divider }}
        size={16}
        thickness={4}
        value={100}
        variant="determinate"
        aria-hidden
      />
      <CircularProgress
        sx={{ position: 'absolute', left: 0 }}
        size={16}
        thickness={4}
        variant="determinate"
        value={progress}
      />
    </Box>
  ) : (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignSelf: 'center',
      }}
    >
      <CircularProgress
        sx={{ color: (theme) => theme.palette.divider }}
        size={16}
        thickness={4}
        value={100}
        variant="determinate"
        aria-hidden
      />
      <CircularProgress
        sx={{ position: 'absolute', left: 0 }}
        disableShrink
        size={16}
        thickness={4}
        variant="indeterminate"
      />
    </Box>
  );
  const titleElement = title && (
    <Typography sx={{ wordWrap: 'break-word' }} variant="h4">
      {title}
    </Typography>
  );

  return (
    <Box
      role="listitem"
      sx={{
        // Ensures we don't have a top double border
        '&:not(:first-of-type)': {
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Stack
        direction="row"
        sx={mixinSx(
          {
            alignItems: 'center',
            gap: 2,
            padding: (theme) => theme.spacing(1.5, 2.5),
          },
          icon === 'loading'
            ? {
                // When progress bar hides but we're still processing, add space to offset it
                backgroundColor: (theme) => theme.palette.primary.light,
              }
            : null
        )}
      >
        <Box
          sx={{
            flex: '1 1 auto',
            display: 'grid',
            gridTemplateColumns: '16px 1fr',
            alignItems: 'center',
            columnGap: 1.5,
          }}
        >
          {iconComponent}
          {endAdornment ? (
            <Stack direction="row" alignItems="baseline" gap={1}>
              {titleElement}
              {endAdornment}
            </Stack>
          ) : (
            titleElement
          )}
          <Typography
            variant="body3"
            sx={{
              color: (theme) => theme.palette.text.secondary,
              wordWrap: 'break-word',
              gridColumn: '2',
            }}
          >
            {subtitle}
          </Typography>
        </Box>
        <Stack gap="1px" direction="row">
          {actions}
        </Stack>
      </Stack>
      {children && (
        <Box
          sx={{
            paddingLeft: 6,
            paddingRight: 2,
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}
